<template>
  <DsButton
    :loading-label="getLoaderText"
    :disabled="isDisabled"
    :size="size"
    :outline="outline"
    :theme-color="themeColor"
    class="ds-social-button"
    data-testid="ds-social-button"
    @click="$emit('click', $event)"
  >
    <span class="ds-social-button-wrap">
      <component
        :is="socialIcon"
        :class="['ds-social-button-icon', loadingSocialIcon]"
        data-testid="ds-social-button-icon"
      />
      <span class="ds-social-button-text-width">
        <span
          v-if="!loading"
          class="ds-social-button-text"
          data-testid="ds-social-button-text"
          >{{ text }}</span
        >
        <span
          v-else
          class="ds-social-button-loading"
          data-testid="ds-social-button-loading"
          >{{ getLoaderText }}</span
        >
      </span>
    </span>
  </DsButton>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { DefineComponent } from "vue";
import type { PropType } from "vue";
import { useI18n } from "vue-i18n";

import DsButton from "../button/DsButton.vue";
import DsInfoCircleSolidIcon from "../icon/alert/DsInfoCircleSolidIcon.vue";
import DsAppleLogoIcon from "../icon/social/DsAppleLogoIcon.vue";
import DsFacebookLogoIcon from "../icon/social/DsFacebookLogoIcon.vue";
import DsGoogleLogoIcon from "../icon/social/DsGoogleLogoIcon.vue";
import DsInstagramLogoIcon from "../icon/social/DsInstagramLogoIcon.vue";
import DsLinkedinLogoIcon from "../icon/social/DsLinkedinLogoIcon.vue";
import DsTwitterLogoIcon from "../icon/social/DsTwitterLogoIcon.vue";
import DsYouTubeLogoIcon from "../icon/social/DsYouTubeLogoIcon.vue";

import type { DsIconSocialName, DsThemeColor, DsThemeSize } from "../../types/";

defineOptions({
  name: "DsSocialButton",
});

const { t } = useI18n();

defineEmits<{
  click: [Event];
}>();

const props = defineProps({
  name: {
    type: String as PropType<DsIconSocialName>,
    default: "google",
  },
  alt: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingLabel: {
    type: String,
    default: () => {
      const { t } = useI18n();

      return t("ds.component.button.loading");
    },
  },
  outline: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String as PropType<DsThemeSize>,
    required: false,
    default: "md",
  },
  themeColor: {
    type: String as PropType<DsThemeColor>,
    required: false,
    default: "black",
  },
});

const socialIcon = computed((): DefineComponent => {
  if (props.name === "apple") {
    return DsAppleLogoIcon as unknown as DefineComponent;
  } else if (props.name === "facebook") {
    return DsFacebookLogoIcon as unknown as DefineComponent;
  } else if (props.name === "google") {
    return DsGoogleLogoIcon as unknown as DefineComponent;
  } else if (props.name === "instagram") {
    return DsInstagramLogoIcon as unknown as DefineComponent;
  } else if (props.name === "linkedin") {
    return DsLinkedinLogoIcon as unknown as DefineComponent;
  } else if (props.name === "twitter") {
    return DsTwitterLogoIcon as unknown as DefineComponent;
  } else if (props.name === "youtube") {
    return DsYouTubeLogoIcon as unknown as DefineComponent;
  }

  return DsInfoCircleSolidIcon as unknown as DefineComponent;
});

const isDisabled = computed((): boolean => {
  return props.loading;
});

const loadingSocialIcon = computed((): string => {
  return props.loading ? "animate-pulse" : "";
});

const getLoaderText = computed((): string => {
  return props.loadingLabel
    ? props.loadingLabel
    : t("ds.component.button.loading");
});
</script>

<style lang="scss" scoped>
.ds-social-button {
  &-wrap {
    @apply flex items-center justify-center space-x-5;
  }

  &-icon {
    @apply flex flex-shrink-0 h-8 w-8;
  }

  &-text {
    @apply flex justify-start;

    &-width {
      @apply w-[180px];
    }
  }

  &-loading {
    @apply flex justify-start;
  }
}
</style>
